import React, { useEffect, useState } from 'react';
import { WifiOff, RefreshCcw, Home, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ConnectionTerminated = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F8] via-white to-[#FFB6C1]/10 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white/60 backdrop-blur-xl border border-pink-100 p-10 rounded-[3rem] shadow-2xl text-center space-y-8">
        <div className="relative">
          <div className="w-24 h-24 bg-pink-100/50 rounded-3xl flex items-center justify-center mx-auto mb-6 transform -rotate-12 hover:rotate-0 transition-transform">
            <WifiOff className="w-12 h-12 text-[#FF6B9D]" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center animate-bounce">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-3xl font-bold text-gray-900 leading-tight">Connection Terminated</h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Oh no! It looks like your connection has drifted away. 
            Check your internet settings and we'll be ready when you're back.
          </p>
        </div>

        <div className="p-4 bg-pink-50/50 rounded-2xl border border-pink-100/50">
          <div className="flex items-center justify-center space-x-3 text-sm text-gray-600">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="font-medium">{isOnline ? 'Status: Online - Connection restored!' : 'Status: Offline - Waiting for signal...'}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
          {isOnline ? (
            <Button
              onClick={() => window.location.reload()}
              className="bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-full px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all flex-1"
            >
              <RefreshCcw className="w-5 h-5 mr-2" />
              Reconnect Now
            </Button>
          ) : (
            <Button
              onClick={() => window.location.reload()}
              className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full px-10 py-6 text-lg transition-all flex-1"
            >
              <RefreshCcw className="w-5 h-5 mr-2 animate-spin-slow" />
              Trying to Reconnect...
            </Button>
          )}
          <Button
            asChild
            variant="ghost"
            className="text-gray-500 hover:text-[#FF6B9D] hover:bg-pink-50 rounded-full px-6 py-6"
          >
            <Link to="/">
              <Home className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ConnectionTerminated;
